import * as React from "react";
import {cx} from "emotion";
import {FontAwesomeIcon}                from "@fortawesome/react-fontawesome";

import "./menu-mobile.scss";

import {AnyAction}      from "@Common/react/any-action";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {UseState}       from "@Common/react/use-state";
import {Dropdown}       from "@Common/ui-components/dropdown/dropdown";

import {getAbsLink}    from "../../../../common/get-abs-link";
import {COMPANY_LEVEL} from "../../../../common/logic/user-level";
import {OFFICE_LEVEL}  from "../../../../common/logic/user-level";
import {USER_LEVEL}    from "../../../../common/logic/user-level";

import {agentItems,
        branchItems,
        companyItems} from "./menu";

const ICON_DEFAULT = "faFolderOpen";

export const MenuMobile = ({active, activeTab, userLevels, navBlock}) => cs(
    consumeContext("routerHistory"),
    consumeContext("accountMenu"),  
    ["accountMenuMobile", ({accountMenu}, next) => next(
        accountMenu ? (
            accountMenuItems(structuredClone(accountMenu))
        ) : (
            placeHolderMenuItems(userLevels.userLevel)
        )
    )],
    ['menuItemSelected', ({}, next) => UseState({
        initValue: active,
        next,
    })],
    ({accountMenuMobile, menuItemSelected, routerHistory}) => (
        <div className="menu-mobile-KOH">
            {Dropdown({
                renderToggle: ({domRef, showExpand, showingExpand}) => (
                    <a
                        className={cx("menu-mobile-render-toggle", {expanded: showingExpand})}
                        xd-qa-type="dropdown"
                        xd-qa-name="menu-mobile"
                        ref={domRef}
                        onClick={async (e) => {
                            e.preventDefault();
                            if (await navBlock?.block() === true) {
                                return;
                            }
                            showExpand(!showingExpand);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={require('@fortawesome/free-solid-svg-icons')[menuHeaderIcon(accountMenuMobile, active)]}
                            className='icon-left'
                        />
                        <div className="menu-mobile-header" >
                            {menuHeaderText(accountMenuMobile, active, activeTab)}
                        </div>
                        <i className={`fa icon-right ${showingExpand ? "fa-caret-up" : "fa-caret-down"}`}></i>
                    </a>
                ),

                renderExpand: ({close, domRef, position}) => (
                    <div
                        className="menu-mobile-render-expand"
                        style={position}
                        ref={domRef}
                    >
                        {menuItems(accountMenuMobile, active, activeTab, menuItemSelected, routerHistory)}
                        {AnyAction({action: close})}
                    </div>
                )
            })}
        </div>
    )
);

const placeHolderMenuItems = (userLevel) => {
    switch(userLevel) {
        case COMPANY_LEVEL:
            return companyItems
        case OFFICE_LEVEL:
            return branchItems
        case USER_LEVEL:
        default:
            return agentItems
    }
}

const makeItemSelectedFilter = (selectedName:string) => (
    (item) => (
        selectedName === item.name
        || (
            GROUPS_TREATED_AS_ITEMS.includes(item.name)
            && item.subMenu?.find(subItem => subItem.configPath === `${selectedName}Menu`)
        )
    )
);

const menuHeaderText = (accountMenuMobile, active, activeTab) => {
    let text = ''
    const itemIsSelected = makeItemSelectedFilter(active);
    accountMenuMobile.forEach(item => {
        if(itemIsSelected(item)) {
            text+= item.linkLabel
            if(item?.subMenu) {
                const subMenuItems = item.subMenu
                subMenuItems.forEach(subItem => {
                    if(
                        subItem.name?.toLowerCase() === activeTab.toLowerCase()
                        || subItem.configPath === `${active}Menu`
                    ) {
                        text+= ` / ${subItem.linkLabel}`
                    }
                })
            }
        }
    })

    return text
}

const menuHeaderIcon = (accountMenuMobile, active) => {
    let icon = ICON_DEFAULT
    const itemIsSelected = makeItemSelectedFilter(active);
    accountMenuMobile.forEach(item => {
        if(itemIsSelected(item)) {
            icon = item.icon
        }
    })
    return icon
}

const accountMenuItems = (accountMenu) => {
    if(!accountMenu) {
        return null
    }

    const unwrappedAccountMenu = Object.keys(accountMenu).map((key) => [key, accountMenu[key]])
    extractDropdownItems(unwrappedAccountMenu)

    const items = []
    const indexItemProperties = 1

    unwrappedAccountMenu.forEach(item => {
        if("object" !== typeof item[indexItemProperties]) {
            return
        }
        if(item.length > indexItemProperties) {
            let itemWithName = item[indexItemProperties]
            if(itemSubMenuExtraneous.find((deleteSubMenu) => deleteSubMenu === item[0])) {
                delete item[indexItemProperties]['subMenu']
            }
            itemWithName['name'] = item[0] ? item[0].replace('Menu', '') : item[indexItemProperties]?.linkLabel
            items.push(itemWithName)
        }
    })

    return items
}

const GROUPS_TREATED_AS_ITEMS = ["programDefaultsDropdown"];

const extractDropdownItems = (unwrappedAccountMenu) => {
    const [indexItemName, indexItemProperties] = [0,1]
    unwrappedAccountMenu.forEach((menuItem, i) => {
        if('dropdownItems' in menuItem[indexItemProperties]) {
            if (GROUPS_TREATED_AS_ITEMS.map(name => `${name}Menu`).includes(menuItem[indexItemName])) {
                menuItem[indexItemProperties]['subMenu'] = menuItem[indexItemProperties]['dropdownItems'];
            } else {
                menuItem[indexItemProperties]['dropdownItems'].forEach((dropdownItem, j) => {
                    unwrappedAccountMenu.splice(i + j + 1, 0, [dropdownItem['configPath'], dropdownItem])
                });
                unwrappedAccountMenu.splice(i, 1); // remove dropdown parent
            }
        }
    })
}

const itemSubMenuExtraneous = [
    'myProfileMenu',
    'officeManagementMenu',
    'userManagementMenu',
];

const menuItems = (accountMenuMobile, active, activeTab, menuItemSelected, routerHistory) => {
    const itemIsSelected = makeItemSelectedFilter(menuItemSelected.value);

    return accountMenuMobile.map(item => (
        <React.Fragment key={`${item.linkLabel}_${item.linkIndex}`} >
            <div
                className={cx("menu-mobile-item", {selected: itemIsSelected(item)})}
                xd-qa-type='link'
                xd-qa-name={item.linkLabel}
                onClick={(e) => {
                    e.stopPropagation()
                    if(!item.subMenu) {
                        if(item.link) {
                            routerHistory.push(item.link)
                        }
                        return
                    }

                    let current = itemIsSelected(item)
                    ?
                    null
                    :
                    item.name
                    menuItemSelected.onChange(current)
                }}
            >
                <FontAwesomeIcon
                    icon={require('@fortawesome/free-solid-svg-icons')[item.icon || ICON_DEFAULT]}
                    className='menu-mobile-icon left'
                />
                <div className='menu-mobile-text'>
                    {item.linkLabel}
                </div>
                {
                    <FontAwesomeIcon
                        icon={require('@fortawesome/free-solid-svg-icons')
                            [
                            itemIsSelected(item)
                            ?
                            'faCaretUp'
                            :
                            'faCaretDown'
                        ]}
                        className={cx("menu-mobile-icon", "right", {'no-submenu': !item.subMenu})}
                    />
                }
            </div>
            {
                (item.subMenu && itemIsSelected(item))
                ?
                <div className='menu-mobile-submenu'>
                {
                    subMenu(item, active, activeTab, menuItemSelected, routerHistory)
                }
                </div>
                :
                null
            }
        </React.Fragment>
    ))
}

const subMenu = (item, active, activeTab, menuItemSelected, routerHistory) => {
    const itemsSubmenu = item.subMenu
    return itemsSubmenu.map((itemSubmenu, j) => (
        <div key={`${itemSubmenu.linkLabel}_${j}`}
            className={
                (itemSubmenu.name === activeTab &&
                item.name === active)
                ?
                'menu-mobile-submenu-item selected'
                :
                'menu-mobile-submenu-item'
            }
            onClick={() => {
                menuItemSelected.onChange(item.linkIndex)
                if(itemSubmenu.link) {
                    routerHistory.push(itemSubmenu.link)
                }
            }}
        >
            <FontAwesomeIcon
                icon={require('@fortawesome/free-solid-svg-icons')['faLongArrowAltRight']}
                className='menu-mobile-submenu-item-icon'
            />
            <div className='menu-mobile-submenu-item-text' >
            {itemSubmenu.linkLabel}
            </div>
        </div>
    ))
}
